/**
 * Load an image element from a File or Blob.
 */
export function loadImageFromFile(file: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Render a cropped region of an image to a JPEG data URI.
 *
 * The crop is defined by scale + offset relative to the crop viewport.
 * The image is drawn at `scale` with the given pixel offsets so that
 * the visible portion of the viewport becomes the output.
 *
 * @param img          Source image element
 * @param scale        Display scale (1 = image fits viewport width)
 * @param offsetX      Horizontal offset in display pixels (negative = panned right)
 * @param offsetY      Vertical offset in display pixels (negative = panned down)
 * @param viewportW    Viewport width in display pixels
 * @param viewportH    Viewport height in display pixels
 * @param outputWidth  Output canvas width
 * @param outputHeight Output canvas height
 */
export function cropToDataUri(
  img: HTMLImageElement,
  scale: number,
  offsetX: number,
  offsetY: number,
  viewportW: number,
  viewportH: number,
  outputWidth = 480,
  outputHeight = 270,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext('2d')!;

  // The image is displayed at: displayW = img.naturalWidth * scale, displayH = img.naturalHeight * scale
  // with offset (offsetX, offsetY) from the viewport top-left.
  // The visible source rectangle in natural-image pixels:
  const sx = -offsetX / scale;
  const sy = -offsetY / scale;
  const sw = viewportW / scale;
  const sh = viewportH / scale;

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outputWidth, outputHeight);
  return canvas.toDataURL('image/jpeg', 0.85);
}
